import config from '@/config';
import { apiRequest } from '@/lib/api';
import type {
  AppointmentAttendanceHint,
  AppointmentAttendanceListData,
  DynamicForm,
  FormAppointmentInstructorAssignment,
  FormSubmissionDetail,
  FormSubmissionsListData,
  FormsListPagination,
  RescheduleFeeWaiver,
} from '@/types/dynamic-form';
import type { FormDefinition } from '@/types/form-definition';

function buildQuery(filters?: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params.append(key, String(value));
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const dynamicFormsApi = {
  list: (filters?: {
    page?: number;
    limit?: number;
    status?: 'draft' | 'published' | 'archived';
    search?: string;
  }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { forms: DynamicForm[]; pagination: FormsListPagination };
    }>(`/api/admin/forms${buildQuery(filters)}`),

  get: (formId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { form: DynamicForm };
    }>(`/api/admin/forms/${encodeURIComponent(formId)}`),

  create: (payload: { title: string; slug?: string; description?: string }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { form: DynamicForm };
    }>('/api/admin/forms', { method: 'POST', body: payload }),

  patch: (
    formId: string,
    payload: Partial<{
      title: string;
      slug: string;
      description: string | null;
      status: DynamicForm['status'];
    }>
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { form: DynamicForm };
    }>(`/api/admin/forms/${encodeURIComponent(formId)}`, { method: 'PATCH', body: payload }),

  saveDraft: (formId: string, definition: FormDefinition) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { form: DynamicForm };
    }>(`/api/admin/forms/${encodeURIComponent(formId)}/draft`, {
      method: 'PUT',
      body: definition,
    }),

  publish: (formId: string, body?: { definition?: FormDefinition }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { form: DynamicForm };
    }>(`/api/admin/forms/${encodeURIComponent(formId)}/publish`, {
      method: 'POST',
      body: body ?? {},
    }),

  remove: (formId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: Record<string, never>;
    }>(`/api/admin/forms/${encodeURIComponent(formId)}`, { method: 'DELETE' }),

  listRescheduleFeeWaivers: (formId: string, filters?: { userId?: string }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { items: RescheduleFeeWaiver[] };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/reschedule-fee-waivers${buildQuery(filters)}`
    ),

  createRescheduleFeeWaiver: (
    formId: string,
    body: {
      appointmentFieldId: string;
      userId: string;
      reason: string;
      expiresAt?: string | null;
    }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { id: string };
    }>(`/api/admin/forms/${encodeURIComponent(formId)}/reschedule-fee-waivers`, {
      method: 'POST',
      body,
    }),

  revokeRescheduleFeeWaiver: (formId: string, waiverId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: Record<string, never>;
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/reschedule-fee-waivers/${encodeURIComponent(waiverId)}`,
      { method: 'DELETE' }
    ),

  listSubmissions: (
    formId: string,
    filters?: {
      page?: number;
      limit?: number;
      definitionVersion?: number;
      status?: 'completed' | 'spam' | 'abandoned';
    }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: FormSubmissionsListData;
    }>(`/api/admin/forms/${encodeURIComponent(formId)}/submissions${buildQuery(filters)}`),

  getSubmission: (formId: string, submissionId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { submission: FormSubmissionDetail };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`
    ),

  deleteSubmission: (formId: string, submissionId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: Record<string, never>;
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`,
      { method: 'DELETE' }
    ),

  /** Bookings for fields with `trackAttendance` in a published version (see docs/ADMIN-PORTAL-APPOINTMENT-ATTENDANCE.md). */
  listAppointmentAttendanceBookings: (
    formId: string,
    filters?: {
      page?: number;
      limit?: number;
      fieldId?: string;
      statusTab?: 'confirmed' | 'cancelled' | 'all';
      attendanceStatus?: 'pending' | 'attended' | 'not_attended';
      submissionId?: string;
      studentUserId?: string;
      slotStartFrom?: string;
      slotStartTo?: string;
      search?: string;
    }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: AppointmentAttendanceListData;
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/bookings${buildQuery(filters)}`
    ),

  patchAppointmentAttendance: (
    formId: string,
    bookingId: string,
    body: { outcome: 'attended' | 'not_attended' }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        booking: Record<string, unknown>;
        attendance: AppointmentAttendanceHint;
      };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/bookings/${encodeURIComponent(bookingId)}/attendance`,
      { method: 'PATCH', body }
    ),

  voidAppointmentAttendance: (
    formId: string,
    bookingId: string,
    body: { reason: string }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        booking: Record<string, unknown>;
        attendance: AppointmentAttendanceHint;
      };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/bookings/${encodeURIComponent(bookingId)}/attendance/void`,
      { method: 'POST', body }
    ),

  /** Slot list `data.scheduleTimeZone` matches published field config (see ADMIN-PORTAL-APPOINTMENT-TIMEZONE.md). */
  listAppointmentSlots: (
    formId: string,
    fieldId: string,
    filters?: { page?: number; limit?: number }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        scheduleTimeZone?: string | null;
        slots?: unknown[];
        pagination?: FormsListPagination;
      };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/${encodeURIComponent(fieldId)}/slots${buildQuery(filters)}`
    ),

  listInstructorAssignments: (formId: string, fieldId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { assignments: FormAppointmentInstructorAssignment[] };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/${encodeURIComponent(fieldId)}/instructor-assignments`
    ),

  createInstructorAssignment: (
    formId: string,
    fieldId: string,
    body: {
      instructorAdminId: string;
      activeFrom: string;
      activeTo?: string | null;
      applyToExisting?: boolean;
    }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { assignment: FormAppointmentInstructorAssignment };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/${encodeURIComponent(fieldId)}/instructor-assignments`,
      { method: 'POST', body }
    ),

  patchInstructorAssignment: (
    formId: string,
    fieldId: string,
    assignmentId: string,
    body: { activeTo: string }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { assignment: FormAppointmentInstructorAssignment };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/${encodeURIComponent(fieldId)}/instructor-assignments/${encodeURIComponent(assignmentId)}`,
      { method: 'PATCH', body }
    ),

  /** Removes the assignment row only; does not clear `instructorAdminId` on existing bookings/slots. */
  deleteInstructorAssignment: (formId: string, fieldId: string, assignmentId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { deleted: boolean; assignmentId: string };
    }>(
      `/api/admin/forms/${encodeURIComponent(formId)}/appointment/${encodeURIComponent(fieldId)}/instructor-assignments/${encodeURIComponent(assignmentId)}`,
      { method: 'DELETE' }
    ),
};

/** CSV export returns raw bytes, not JSON — use cookies for auth. */
export async function downloadFormSubmissionsCsv(
  formId: string,
  options?: { definitionVersion?: number }
): Promise<void> {
  const base = (config.apiUrl || '').replace(/\/$/, '');
  const params = new URLSearchParams();
  if (options?.definitionVersion != null) {
    params.set('definitionVersion', String(options.definitionVersion));
  }
  const qs = params.toString();
  const path = `/api/admin/forms/${encodeURIComponent(formId)}/submissions/export${qs ? `?${qs}` : ''}`;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  const response = await fetch(url, { method: 'GET', credentials: 'include' });
  if (!response.ok) {
    let msg = `Export failed (${response.status})`;
    try {
      const j = await response.json();
      if (typeof j?.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }

  const blob = await response.blob();
  const dispo = response.headers.get('Content-Disposition');
  let filename = `form-submissions.csv`;
  const m = /filename="?([^";]+)"?/i.exec(dispo || '');
  if (m?.[1]) filename = m[1].trim();

  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
